import React from "react";
import { CalendarRange, Infinity as InfinityIcon, UserCheck } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDate } from "@/lib/time";

type F = { booking_mode: string; repeat_days: number | string; start: string; skip_weekdays: number[] };

export const BookingModeFields = ({ f, set, isNew }: { f: F; set: (k: string, v: unknown) => void; isNew: boolean }) => {
  const { t, lang } = usePrefs();
  const n = Math.min(30, Math.max(1, Number(f.repeat_days) || 1));
  const base = f.start?.length >= 10 ? new Date(f.start.slice(0, 10) + "T12:00:00") : null;
  const last = base ? new Date(base.getTime() + (n - 1) * 864e5).toISOString() : "";
  const skip = f.skip_weekdays || [];
  const count = base ? 1 + Array.from({ length: n - 1 }, (_, k) => new Date(base.getTime() + (k + 1) * 864e5).getDay()).filter((d) => !skip.includes(d)).length : n;
  const toggleDay = (d: number) => set("skip_weekdays", skip.includes(d) ? skip.filter((x) => x !== d) : [...skip, d]);
  const dayName = (d: number) => new Date(Date.UTC(2024, 0, 7 + d, 12)).toLocaleDateString(lang === "ar" ? "ar-IQ" : "en-GB", { weekday: "short", timeZone: "UTC" });
  const modes = [{ k: "open", Icon: InfinityIcon }, { k: "single", Icon: UserCheck }] as const;
  return (
    <div className="rounded-3xl border bg-card p-5 space-y-4" data-testid="event-booking-mode-section">
      <div className="space-y-2">
        <p className="text-sm font-bold">{t("bm.title")}</p>
        <div className="grid grid-cols-2 gap-2">
          {modes.map(({ k, Icon }) => (
            <button key={k} type="button" aria-pressed={f.booking_mode === k} onClick={() => set("booking_mode", k)} data-testid={`event-mode-${k}`}
              className={`rounded-2xl border p-3 text-start transition-[background-color] ${f.booking_mode === k ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-accent"}`}>
              <span className="text-sm font-bold inline-flex items-center gap-1.5"><Icon className="h-4 w-4" />{t(`bm.${k}`)}</span>
              <span className="block text-[11px] opacity-75 mt-0.5 leading-snug">{t(`bm.${k}Hint`)}</span>
            </button>
          ))}
        </div>
      </div>
      {isNew && (
        <div className="space-y-1.5" data-testid="event-repeat-section">
          <p className="text-sm font-bold inline-flex items-center gap-1.5"><CalendarRange className="h-4 w-4 text-primary" />{t("bm.repeat")}</p>
          <div className="flex items-center gap-2">
            <Input type="number" min={1} max={30} dir="ltr" value={f.repeat_days} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("repeat_days", e.target.value)} onBlur={() => set("repeat_days", n)} data-testid="event-repeat-days" className="h-11 w-24 rounded-2xl" />
            <span className="text-sm text-muted-foreground">{t("bm.days")}</span>
            {n > 1 && last && <span data-testid="event-repeat-until" className="text-xs font-bold text-primary">{t("bm.until").replace("{d}", fmtDate(last, lang))}</span>}
          </div>
          <p className="text-xs text-muted-foreground">{t("bm.repeatHint")}</p>
          {n > 1 && (
            <div className="space-y-1.5 pt-1" data-testid="event-skip-days">
              <p className="text-xs font-bold">{t("bm.skip")}</p>
              <div className="flex flex-wrap gap-1.5">
                {[6, 0, 1, 2, 3, 4, 5].map((d) => (
                  <button key={d} type="button" aria-pressed={skip.includes(d)} onClick={() => toggleDay(d)} data-testid={`event-skip-day-${d}`}
                    className={`h-9 min-w-[3.25rem] px-2 rounded-full text-xs font-bold border transition-[background-color] ${skip.includes(d) ? "bg-primary/10 text-primary border-primary line-through" : "bg-background hover:bg-accent"}`}>{dayName(d)}</button>
                ))}
              </div>
              {base && <p data-testid="event-repeat-count" className="text-xs font-bold text-primary">{t("bm.count").replace("{n}", String(count))}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
